import isPermissionEnabled from "common/isPermissionEnabled"
import { SUPERADMIN_NAV_LINKS } from "components/SuperAdmin/superadminNav"
import { ROUTES } from "constants/routes"
import { hasPlanSocialLoginAccess } from "utils/planSocialLogins"
import { getLoginRedirect } from "utils/routeUtils"

const DASHBOARD_PERMISSION_IDS = [
  "dashboard_1",
  "dashboard_2",
  "dashboard_3",
  "dashboard_4",
  "dashboard_5",
  "dashboard_6",
  "dashboard_7",
  "dashboard_8",
  "dashboard_9",
]

const ALWAYS_ACCESSIBLE_PREFIXES = [
  "/profile",
  "/change-password",
  "/my-preference",
  "/export-requests",
]

/** Tenant with no subscription plan — settings + billing only (backend sends permissions: []). */
const NO_PLAN_TENANT_ACCESSIBLE_PREFIXES = [
  ROUTES.BILLING_DASHBOARD,
  ROUTES.BILLING_PLANS,
  ROUTES.BILLING_CHECKOUT,
  ROUTES.BILLING_INVOICES,
  ROUTES.BILLING_INVOICE_DETAILS,
]

const NO_PLAN_TENANT_DEFAULT_ROUTE = ROUTES.MY_PREFERENCE

/** When no sidebar module is allowed — always-accessible settings/profile screens. */
const getNoModuleAccessFallback = (ctx = {}) => {
  if (perm(19, "referral_1")(ctx)) {
    return "/app-settings"
  }
  return ROUTES.MY_PREFERENCE
}

const normalizePath = pathname => (pathname || "").replace(/\/+$/, "") || "/"

const perm =
  (categoryId, permissionId) =>
  ({ userRole, accountType, accountScope }) =>
    isPermissionEnabled(
      userRole,
      categoryId,
      permissionId,
      accountType,
      accountScope
    )

const hasDashboardAccess = ctx =>
  DASHBOARD_PERMISSION_IDS.some(id => perm(1, id)(ctx))

const hasFaqAccess = ctx =>
  perm(18, "faq_1")(ctx) || perm(54, "rider_faq_1")(ctx)

const hasMenusAccess = ctx =>
  perm(4, "menus_1")(ctx) || perm(4, "item_tag_1")(ctx)

const hasCustomersAccess = ctx =>
  perm(7, "customersPermission_1")(ctx) ||
  perm(7, "customersPermission_5")(ctx)

const hasIntegrationsAccess = ctx => perm(48, "tenant_integrations_1")(ctx)

const hasSocialAuthSettingsAccess = ctx =>
  perm(53, "social_auth_settings_1")(ctx) &&
  hasPlanSocialLoginAccess(ctx?.planSocialLogins)

const hasBillingAccess = ctx => {
  const noPlanTenant =
    Array.isArray(ctx?.userRole?.permissions) &&
    ctx.userRole.permissions.length === 0
  return noPlanTenant || perm(51, "billing_1")(ctx)
}

/** Sidebar order — first match wins for post-login / unauthorized redirects. */
const TENANT_NAV_ENTRIES = [
  { path: ROUTES.DASHBOARD, canAccess: hasDashboardAccess },
  { path: "/banners", canAccess: perm(2, "inAppContent_1") },
  { path: "/offer-banners", canAccess: perm(49, "offerBanner_1") },
  { path: "/menus/menu-setup", canAccess: hasMenusAccess },
  { path: "/menus/item-tags", canAccess: perm(4, "item_tag_1") },
  { path: "/roles", canAccess: perm(3, "roles_1") },
  { path: "/branches", canAccess: perm(5, "branchLocations_1") },
  { path: "/users", canAccess: perm(6, "usersPermission_1") },
  { path: "/user-activity", canAccess: perm(36, "user_activity_1") },
  { path: "/customers", canAccess: hasCustomersAccess },
  { path: "/support-issues", canAccess: perm(17, "customer_support_1") },
  { path: "/countries", canAccess: perm(11, "country_1") },
  { path: "/zones", canAccess: perm(43, "zone_1") },
  { path: ROUTES.ORDERS, canAccess: perm(9, "order_1") },
  { path: "/vat-info", canAccess: perm(12, "tax_1") },
  { path: "/api-logs", canAccess: perm(13, "api_logs_1") },
  { path: "/discounts", canAccess: perm(14, "promotion_1") },
  { path: "/coupons-group", canAccess: perm(14, "promotion_1") },
  { path: "/wallet-coupons", canAccess: perm(46, "coupon_redeem_1") },
  { path: "/app-notifications", canAccess: perm(15, "notification_1") },
  { path: "/sales-reports", canAccess: perm(16, "report_1") },
  { path: "/faq", canAccess: hasFaqAccess },
  { path: "/app-settings", canAccess: perm(19, "referral_1") },
  { path: "/bookings", canAccess: perm(21, "booking_1") },
  { path: "/generate-qr-code", canAccess: perm(22, "qr_code_1") },
  { path: "/ingredients", canAccess: perm(23, "ingredient_1") },
  { path: "/allergens", canAccess: perm(24, "allergen_1") },
  { path: "/rating-questions", canAccess: perm(27, "rating_1") },
  { path: "/api-key", canAccess: perm(28, "api_key_1") },
  { path: "/branch-sync", canAccess: perm(29, "branch_sync_1") },
  { path: "/menu-sync", canAccess: perm(30, "menu_sync_1") },
  { path: "/giftcard", canAccess: perm(37, "gift_card_1") },
  { path: "/vehicles", canAccess: perm(35, "vehicle_1") },
  { path: "/brands", canAccess: perm(52, "brands_1") },
  { path: "/delivery-partners", canAccess: perm(47, "delivery_partners_1") },
  { path: "/settings/integrations", canAccess: hasIntegrationsAccess },
  { path: "/settings/social-auth", canAccess: hasSocialAuthSettingsAccess },
  { path: "/integrations", canAccess: hasIntegrationsAccess },
  { path: ROUTES.BILLING_DASHBOARD, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_PLANS, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_CHECKOUT, canAccess: hasBillingAccess },
  { path: ROUTES.BILLING_INVOICES, canAccess: hasBillingAccess },
]

const flattenSuperAdminNav = () => {
  const entries = []
  SUPERADMIN_NAV_LINKS.forEach(link => {
    if (link.permission) {
      entries.push({ path: link.path, permission: link.permission })
    }
    ;(link.children || []).forEach(child => {
      if (child.permission) {
        entries.push({ path: child.path, permission: child.permission })
      }
    })
  })
  return entries
}

const SUPERADMIN_NAV_ENTRIES = flattenSuperAdminNav()

const TENANT_ROUTE_RULES = TENANT_NAV_ENTRIES.map(entry => ({
  prefix: entry.path,
  canAccess: entry.canAccess,
}))

const SUPERADMIN_ROUTE_RULES = SUPERADMIN_NAV_ENTRIES.map(entry => ({
  prefix: entry.path,
  canAccess: perm(entry.permission.categoryId, entry.permission.permissionId),
}))

export const isSuperAdminSession = ({ accountType, accountScope } = {}) =>
  accountScope === "super_admin" || accountType === "superadmin"

/** Legacy bootstrap operator — full bypass (matches isPermissionEnabled). */
export const isLegacyBootstrapSuperAdmin = ({
  accountType,
  accountScope,
} = {}) => {
  if (localStorage.getItem("tenantId")) {
    return false
  }
  const scope =
    accountScope ??
    (typeof localStorage !== "undefined"
      ? localStorage.getItem("accountScope")
      : null)
  return accountType === "superadmin" && scope !== "tenant"
}

export const getAuthContextFromStorage = () => {
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
    return {
      userRole: authUser?.role || authUser?.userRole || null,
      accountType:
        localStorage.getItem("accountType") || authUser?.accountType || null,
      accountScope:
        localStorage.getItem("accountScope") ||
        authUser?.accountScope ||
        "tenant",
      planSocialLogins:
        authUser?.planSocialLogins && typeof authUser.planSocialLogins === "object"
          ? authUser.planSocialLogins
          : { google: false, apple: false },
    }
  } catch {
    return {
      userRole: null,
      accountType: null,
      accountScope: "tenant",
      planSocialLogins: { google: false, apple: false },
    }
  }
}

const isResolvedRole = role =>
  role && !Array.isArray(role) && Array.isArray(role.permissions)

/** Prefer Redux role when loaded; fall back to persisted login payload for first paint. */
export const mergeAuthContext = (reduxLogin = {}) => {
  const stored = getAuthContextFromStorage()
  const reduxRole = reduxLogin?.userRole

  return {
    userRole: isResolvedRole(reduxRole) ? reduxRole : stored.userRole,
    accountType: reduxLogin?.accountType ?? stored.accountType,
    accountScope: reduxLogin?.accountScope ?? stored.accountScope,
    planSocialLogins:
      reduxLogin?.planSocialLogins ?? stored.planSocialLogins ?? {
        google: false,
        apple: false,
      },
  }
}

const isAlwaysAccessible = pathname => {
  const normalized = normalizePath(pathname)
  return ALWAYS_ACCESSIBLE_PREFIXES.some(
    prefix => normalized === prefix || normalized.startsWith(`${prefix}/`)
  )
}

const getRouteRules = ctx =>
  isSuperAdminSession(ctx) && !localStorage.getItem("tenantId")
    ? SUPERADMIN_ROUTE_RULES
    : TENANT_ROUTE_RULES

const findRouteRule = (pathname, ctx) => {
  const normalized = normalizePath(pathname)
  const rules = getRouteRules(ctx).sort(
    (a, b) => b.prefix.length - a.prefix.length
  )
  return rules.find(rule => {
    const prefix = normalizePath(rule.prefix)
    return normalized === prefix || normalized.startsWith(`${prefix}/`)
  })
}

const hasRolePermissions = ctx =>
  isResolvedRole(ctx?.userRole) && ctx.userRole.permissions.length > 0

/** Plan-scoped auth returns an empty permissions array when the tenant has no active plan. */
export const isNoPlanTenantContext = ctx => {
  if (isSuperAdminSession(ctx) && !localStorage.getItem("tenantId")) {
    return false
  }
  return (
    Array.isArray(ctx?.userRole?.permissions) &&
    ctx.userRole.permissions.length === 0
  )
}

const isNoPlanAccessiblePath = pathname => {
  const normalized = normalizePath(pathname)
  return NO_PLAN_TENANT_ACCESSIBLE_PREFIXES.some(prefix => {
    const normalizedPrefix = normalizePath(prefix)
    return (
      normalized === normalizedPrefix ||
      normalized.startsWith(`${normalizedPrefix}/`)
    )
  })
}

export const canAccessPath = (pathname, ctx) => {
  if (isAlwaysAccessible(pathname)) {
    return true
  }

  if (isNoPlanTenantContext(ctx)) {
    return isNoPlanAccessiblePath(pathname)
  }

  const normalized = normalizePath(pathname)

  if (isLegacyBootstrapSuperAdmin(ctx)) {
    return normalized.startsWith("/superadmin")
  }

  // Role not hydrated yet (e.g. Redux still `[]`) — deny module routes until resolved.
  if (!isResolvedRole(ctx?.userRole)) {
    const rule = findRouteRule(normalizePath(pathname), ctx)
    return !rule
  }

  if (!hasRolePermissions(ctx)) {
    return false
  }

  if (isSuperAdminSession(ctx) && !localStorage.getItem("tenantId")) {
    if (!normalized.startsWith("/superadmin")) {
      return false
    }
  } else if (normalized.startsWith("/superadmin")) {
    return false
  }

  const rule = findRouteRule(normalized, ctx)
  if (!rule) {
    return true
  }
  return rule.canAccess(ctx)
}

export const getFirstAccessibleRoute = (ctx = {}) => {
  if (isNoPlanTenantContext(ctx)) {
    return NO_PLAN_TENANT_DEFAULT_ROUTE
  }

  const entries = isSuperAdminSession(ctx) && !localStorage.getItem("tenantId")
    ? SUPERADMIN_NAV_ENTRIES.map(entry => ({
        path: entry.path,
        canAccess: perm(
          entry.permission.categoryId,
          entry.permission.permissionId
        ),
      }))
    : TENANT_NAV_ENTRIES

  for (const entry of entries) {
    if (entry.canAccess(ctx)) {
      return entry.path
    }
  }

  return getNoModuleAccessFallback(ctx)
}

export const resolveAuthorizedRedirect = (requestedPath, rawCtx = {}) => {
  const ctx = mergeAuthContext(rawCtx)
  const normalized = normalizePath(requestedPath)
  if (canAccessPath(normalized, ctx)) {
    return normalized
  }
  return getFirstAccessibleRoute(ctx)
}

export const resolvePostLoginRedirect = (search, loginData = {}) => {
  const ctx = mergeAuthContext({
    userRole: loginData?.role || loginData?.userRole || null,
    accountType: loginData?.accountType || null,
    accountScope: loginData?.accountScope || "tenant",
    planSocialLogins: loginData?.planSocialLogins || null,
  })

  let requested = getLoginRedirect(search)

  if (
    isSuperAdminSession(ctx) &&
    !localStorage.getItem("tenantId") &&
    (requested === ROUTES.DASHBOARD ||
      requested === "/" ||
      requested === ROUTES.LOGIN ||
      requested === ROUTES.SA_LOGIN)
  ) {
    requested = ROUTES.SA_DASHBOARD
  }

  return resolveAuthorizedRedirect(requested, ctx)
}

export const getForbiddenRedirectPath = () => {
  const ctx = getAuthContextFromStorage()
  return getFirstAccessibleRoute(ctx)
}

/** @deprecated Prefer resolveAuthorizedRedirect — kept for imports migrating off /forbidden. */
export const getUnauthorizedRedirectPath = (ctx, currentPath) => {
  if (canAccessPath(currentPath, ctx)) {
    return null
  }
  const fallback = getFirstAccessibleRoute(ctx)
  return normalizePath(fallback) === normalizePath(currentPath) ? null : fallback
}
